import { useEffect, useMemo, useState } from "react"
import { PortfolioModelService } from "../service/PortfolioModelService"
import { tables } from "../utils/utils"
import { useSupabase } from "./context/useSupabase"

const aggregateCustomers = (data) => {
  const dates = {}
  const totals = {}

  const aggregated = data.reduce((acc, { strategy, customer, target, class_name, date, amount }) => {
    const groupBy = `${strategy}-${class_name}`
    if (!acc[groupBy]) acc[groupBy] = { strategy, class_name }
    acc[groupBy][customer] = target

    dates[customer] = date.substring(0, 10)

    if (!totals[customer]) totals[customer] = 0
    totals[customer] += amount

    return acc
  }, {})

  return { excel: Object.values(aggregated), dates, totals }
}

export const useIPS = ({ locale, selectedCustomer }) => {
  const supabase = useSupabase()
  const [ips, setIPS] = useState()
  const [dates, setDates] = useState()
  const [totalOnshore, setTotalOnshore] = useState()
  const [totalOffshore, setTotalOffshore] = useState()
  const [totals, setTotals] = useState()
  const [excelAllClients, setExcelAllClients] = useState()
  const [customers, setCustomers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [lastHistoricalTacticalPosition, setLastHistoricalTacticalPosition] = useState([])

  const portfolioModelService = useMemo(() => PortfolioModelService(supabase), [supabase])

  useEffect(() => {
    portfolioModelService.getHistoryTacticalPosition().then(history => {
      const dates = Object.keys(history[0]).filter(date => date.split('-').length === 3)
      dates.sort((a, b) => new Date(b) - new Date(a))
      const lastDate = dates[0]

      const MAP = { VERY_BEARISH: -2, BEARISH: -1, NEUTRAL: 0, BULLISH: 1, VERY_BULLISH: 2 }

      setLastHistoricalTacticalPosition(history.map(item => ({
        assetClass: item.assetClass,
        tacticalPosition: MAP[item[lastDate]],
      })))
    })
  }, [])

  const generateIPSFull = (ips) => {
    let totalOnshore = 0
    let totalOffshore = 0

    ips.forEach((row) => {
      if (row.class_name.includes("BZ")) totalOnshore += row.amount || 0
      else totalOffshore += row.amount || 0
    })

    const totalGlobal = totalOnshore + totalOffshore

    const globalTargetMap = ips.reduce((acc, row) => {
      const key = row.class_name.replace(" BZ", "")

      if (!acc[key]) {
        acc[key] = {
          customer: selectedCustomer,
          class_name: key,
          strategy: row.strategy,
          amount: 0,
          current: 0,
          target: 0,
        }
      }

      const reference = row.class_name.includes("BZ") ? totalOnshore : totalOffshore

      acc[key].amount += row.amount || 0
      acc[key].target += (reference * (row.target || 0)) / totalGlobal
      acc[key].current += (row.amount || 0) / totalGlobal

      return acc
    }, {})

    return [Object.values(globalTargetMap).sort((a, b) => a.class_name.localeCompare(b.class_name)), totalOnshore, totalOffshore]
  }

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true)

      const { data } = await supabase.from(`view_ips_${locale === "all" ? "full" : locale}`).select("*")
      const { excel, dates, totals } = aggregateCustomers(data)

      setIsLoading(false)
      setDates(dates)
      setExcelAllClients(excel)
      setTotals(totals)
      setCustomers(Object.keys(dates).sort())

      const customerIPS = data.filter((row) => row.customer === selectedCustomer)

      const ignoreNormalization = ["Hedge Funds", "Private Investments", "Private Investments BZ"]

      if (locale !== "all") {
        const signalMap = Object.fromEntries(
          lastHistoricalTacticalPosition.map(item => [item.assetClass, item.tacticalPosition])
        )

        const tau = 0.025

        const normalizeFlags = customerIPS.map(item => !ignoreNormalization.includes(item.class_name) ? 1 : 0)

        const w_tilt = customerIPS.map(item => {
          const signal = signalMap[item.class_name] ?? 0
          return (item.target || 0) + tau * signal
        })

        const B = customerIPS.reduce((acc, item, idx) =>
          acc + ((item.target || 0) * normalizeFlags[idx]), 0)

        const S = w_tilt.reduce((acc, val, idx) =>
          acc + (val * normalizeFlags[idx]), 0)

        const m = normalizeFlags.reduce((acc, val) => acc + val, 0)

        const alpha = m > 0 ? (B - S) / m : 0

        return setIPS(() => {
          const adjusted = customerIPS.map((row) => {
            const signal = signalMap[row.class_name] ?? 0
            const w_tilt = (row.target || 0) + tau * signal
            const isNormalizable = !ignoreNormalization.includes(row.class_name)

            let w_norm = isNormalizable ? w_tilt + alpha : w_tilt

            const min = row.class_name === "Cash" ? 0.05 : isNormalizable ? 0 : -Infinity
            const clipped = Math.max(w_norm, min)

            return {
              ...row,
              w_tilt,
              target_tactical: clipped,
              isNormalizable,
              min,
              overflow: clipped - w_norm
            }
          })

          const totalOverflow = adjusted.reduce((acc, row) => acc + (row.isNormalizable ? row.overflow : 0), 0)

          const eligible = adjusted.filter(row =>
            row.isNormalizable && row.target_tactical > row.min
          )

          const sumAboveMin = eligible.reduce((acc, row) =>
            acc + (row.target_tactical - row.min), 0
          )

          let final = adjusted.map(row => {
            if (!row.isNormalizable || row.target_tactical <= row.min || sumAboveMin === 0) {
              return row
            }

            const redistribution = (row.target_tactical - row.min) / sumAboveMin * totalOverflow
            return {
              ...row,
              target_tactical: row.target_tactical - redistribution
            }
          })

          const EPS = 1e-12
          const sumIgnored = final
            .filter(r => !r.isNormalizable)
            .reduce((acc, r) => acc + (r.target_tactical || 0), 0)

          const cap = Math.max(0, 1 - sumIgnored)

          const norm = final.filter(r => r.isNormalizable)
          const sumNorm = norm.reduce((acc, r) => acc + r.target_tactical, 0)

          const deviation = cap - sumNorm

          if (Math.abs(deviation) > EPS) {
            const adjustables = norm.filter(r => r.target_tactical > r.min)
            const base = adjustables.reduce((acc, r) => acc + (r.target_tactical - r.min), 0)

            if (base > EPS) {
              final = final.map(r => {
                if (!r.isNormalizable || r.target_tactical <= r.min) return r
                const weight = (r.target_tactical - r.min) / base
                return { ...r, target_tactical: r.target_tactical + deviation * weight }
              })
            } else {
              const n = norm.length
              const equal = n > 0 ? (cap - norm.reduce((a, x) => a + x.min, 0)) / n : 0
              final = final.map(r => {
                if (!r.isNormalizable) return r
                return { ...r, target_tactical: r.min + Math.max(equal, 0) }
              })
            }
          }

          return final
        })
      }

      const [ipsFull, totalOnshore, totalOffshore] = generateIPSFull(customerIPS)

      if (!ipsFull.length) return () => { }

      setTotalOnshore(totalOnshore)
      setTotalOffshore(totalOffshore)
      setIPS(ipsFull?.sort((a, b) => a.class_name?.localeCompare(b.class_name)))
    }

    fetch()
  }, [locale, selectedCustomer])

  return { isLoading, ips, customers, dates, excelAllClients, totals }
}

export const useExpectedReturn = () => {
  const [reload, setReload] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [expectedReturn, setExpectedReturn] = useState([])
  const [date, setDate] = useState()
  const [availableDates, setAvailableDates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const supabase = useSupabase()

  const forceReload = () => setReload((prev) => prev + 1)

  const insertExpectedReturn = (data) => supabase.from(tables.FACT_EXPECTED_RETURN).insert(data).then(forceReload)

  useEffect(() => {
    if (!date && availableDates.length > 0) setDate(availableDates[0])
  }, [availableDates])

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true)

      const { data: dates } = await supabase.from(tables.FACT_EXPECTED_RETURN).select("date").order("date", { ascending: false })
      setAvailableDates(Array.from(dates.reduce((acc, { date }) => acc.add(date), new Set())))
      setExpectedReturn((await supabase.from(tables.FACT_EXPECTED_RETURN).select("*").eq("date", date)).data || [])

      setIsLoading(false)
    }

    fetch()
  }, [reload, date])

  return { expectedReturn, availableDates, showModal, isLoading, date, setShowModal, setDate, insertExpectedReturn }
}
